import * as CL from '@design-system/component-library';
import { type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses.js';
import { seeOptionsMsg, t } from '../../common/i18n/index.js';
import {
  addEmptyFieldValidationError,
  addValidationError,
  convertToErrorStringMap,
} from '../../common/utils/errorUtils.js';
import { POSTAL_CODE_REGEX } from '../../common/utils/validationUtils.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { resetAddressSearchResult, searchAddress } from '../../selfservice/actions/index.js';
import { PostalCodeInput } from '../PostalCodeInput/PostalCodeInput.js';
import { Search } from '../Search/Search.js';
import type { CommonError } from '../../common/types/errors.js';
import type { AddressSearchResponse } from '../../generated/api/models.js';

import './AddressSearch.scss';

export interface AddressSearchMatch {
  addressText: string;
  postalCode: string;
  addressId: string;
}

export interface AddressSearchResult extends AddressSearchResponse {
  postalCode: string;
}

export interface AddressSearchProps {
  addressSearchResult?: AddressSearchResult;
  heading?: ReactNode;
  disabled?: boolean;
  hideSubmitButton?: boolean;
  initialPostalCodeValue?: string;
  initialStreetAddressValue?: string;
  onReset?: () => void;
  onSearch?: (searchText: string, postalCode: string) => void;
  onSubmit: (address: string, postalCode: string) => void;
  minimumCharacters?: number;
}

const getErrorsFromAddressSearch = (postalCode?: string, addressSearchResult?: AddressSearchResult) => {
  const errors: CommonError[] = [];
  if (postalCode && !POSTAL_CODE_REGEX.test(postalCode)) {
    addValidationError(errors, t.BX0B('Enter a 5-digit postal code'), 'postalCode');
  } else if (!addressSearchResult?.match?.addressId) {
    addEmptyFieldValidationError(errors, 'streetAddress');
  }
  return errors;
};

export const AddressSearch = ({
  addressSearchResult,
  heading = <h3 className={dsClass.H3}>{t.ZE18('What address do you need the Internet for?')}</h3>,
  disabled,
  hideSubmitButton = false,
  initialPostalCodeValue,
  initialStreetAddressValue,
  onReset,
  onSearch,
  onSubmit,
  minimumCharacters = 1,
}: AddressSearchProps) => {
  const { siteLanguage } = useContext(SiteContext);
  const dispatch = useDispatch();
  const [postalCode, setPostalCode] = useState(initialPostalCodeValue || '');
  const [validationErrors, setValidationErrors] = useState<{ [s: string]: string }>();
  const streetAddressInput = useRef<HTMLInputElement>(null);
  const [streetSearchValue, setStreetSearchValue] = useState(initialStreetAddressValue || '');

  useEffect(() => {
    if (streetAddressInput?.current && POSTAL_CODE_REGEX.test(postalCode)) {
      streetAddressInput.current.focus();
    }
  }, [postalCode]);

  useEffect(() => {
    const addressSearchErrors = getErrorsFromAddressSearch(postalCode, addressSearchResult);
    setValidationErrors(addressSearchErrors.length ? convertToErrorStringMap(addressSearchErrors) : undefined);
  }, [siteLanguage, addressSearchResult, postalCode]);

  const checkForErrorsAndSubmit = () => {
    const errors = getErrorsFromAddressSearch(postalCode, addressSearchResult);
    if (errors.length) {
      setValidationErrors(convertToErrorStringMap(errors));
    } else {
      onSubmit(streetSearchValue || '', postalCode);
    }
  };

  const onSearchAddress = useCallback<(searchText: string, zipCode: string) => void>(
    (searchText, zipCode) => dispatch(searchAddress(searchText, zipCode)),
    [dispatch]
  );

  const getSearchResults = () => {
    if (addressSearchResult?.matchedStreetNames?.length) {
      return addressSearchResult.matchedStreetNames.map(streetName => ({
        key: streetName,
        displayText: streetName + ' ',
      }));
    } else if (addressSearchResult?.matchedAddresses?.length) {
      return addressSearchResult.matchedAddresses
        .filter(({ addressText }) => addressText !== addressSearchResult?.match?.addressText)
        .map(({ addressId, addressText }) => ({
          key: addressId,
          displayText: addressText,
        }));
    }

    return [];
  };

  return (
    <div className="of-address-search__container">
      {heading}
      <div className="of-address-search__search-row">
        <div className="of-address-search__postal-code">
          <PostalCodeInput
            postalCode={postalCode}
            disabled={disabled}
            setPostalCode={newCode => {
              setStreetSearchValue('');
              setPostalCode(newCode);
              dispatch(resetAddressSearchResult());
              onReset?.();
            }}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
          />
        </div>
        <Search
          value={streetSearchValue}
          setValue={setStreetSearchValue}
          disabled={!POSTAL_CODE_REGEX.test(postalCode) || disabled}
          emptyResultsLabel={t.J6P7('Enter the full address, including the door number')}
          initialValue={initialStreetAddressValue}
          inputLabel={t.GL4R('Street address, e.g. Ritarikatu 42 C 6')}
          inputPlaceholder={t.MX5U('Street address and number')}
          inputRef={streetAddressInput}
          minimumCharacters={minimumCharacters}
          onSearch={searchText => (onSearch ?? onSearchAddress)(searchText.trim(), postalCode)}
          searchResults={getSearchResults()}
          showSuccessIcon={Boolean(streetSearchValue) && !validationErrors?.streetAddress}
          validationError={validationErrors?.streetAddress}
        />
        <div
          className={`of-address-search__search-button-wrapper ${dsClass.PADDING_TOP_1} ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_FLEX_START}`}
        >
          {!hideSubmitButton ? (
            <CL.Button
              className="of-address-search__search-button"
              onClick={checkForErrorsAndSubmit}
              disabled={!postalCode || !streetSearchValue || Boolean(validationErrors) || disabled}
              size="l"
            >
              {t.VQE7(seeOptionsMsg)}
            </CL.Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
